/**
 * SvelteKit クライアントサイドフック
 *
 * PUBLIC_ENABLE_MSW=true の場合、MSWを起動してAPIモックを有効化します。
 */
import { PUBLIC_API_BASE_URL, PUBLIC_ENABLE_MSW } from '$env/static/public';

const shouldEnableMsw =
  PUBLIC_ENABLE_MSW != null
    ? PUBLIC_ENABLE_MSW === 'true'
    : PUBLIC_API_BASE_URL === 'https://dummy.example.com';

declare global {
  interface Window {
    __mswReady?: Promise<void>;
  }
}

/**
 * E2Eテスト用にグローバルに公開するMSWコントロール関数群
 *
 * ブラウザコンソールやPlaywrightから楽観的ロック競合をシミュレートするために使用
 * 例: window.__LOGIMASY_MSW__.enableOptimisticLockConflict(2)
 */
type LogimasyMswControls = {
  enableOptimisticLockConflict: (failureCount?: number, targetId?: string | null) => void;
  disableOptimisticLockConflict: () => void;
  resetOptimisticLockSimulation: () => void;
  setLogoutFailureStatus: (status: number | null) => void;
  getLogoutCallCount: () => number;
  resetLogoutState: () => void;
  enableUnauthenticatedMode: () => void;
  disableUnauthenticatedMode: () => void;
  setUserMeWarningHeader: (header: string | null) => void;
  resetUserMeWarningHeader: () => void;
  setBillingResponse: (response: unknown) => void;
  setBillingStatus: (status: number) => void;
  setBillingWarningHeader: (header: string | null) => void;
  setBillingDelay: (delayMs: number) => void;
  resetBillingState: () => void;
  setExportInstructionCount: (partial: unknown) => void;
  setExportInstructionExpectedRowCountHeader: (value: string | null) => void;
  setExportInstructionActualCsvRowCount: (count: number) => void;
  setExportInstructionExportStatus: (status: number) => void;
  setExportInstructionExportDelay: (delayMs: number) => void;
  setExportInstructionExportError: (enabled: boolean) => void;
  setExportInstructionFailuresRemaining: (count: number) => void;
  resetExportInstructionState: () => void;
};

type LogimasyGlobal = typeof globalThis & {
  __LOGIMASY_MSW__?: LogimasyMswControls;
};

async function startMsw(): Promise<void> {
  const isBrowser = typeof window !== 'undefined';
  if (!shouldEnableMsw || !isBrowser) return;

  try {
    document.cookie = 'XSRF-TOKEN=msw; path=/';
    const { worker } = await import('./mocks/browser');
    await worker.start({
      // E2Eテストで未処理のリクエストを検出しやすくするため 'warn' に設定
      // 実際のAPIへのリクエストは引き続き通過する（bypass動作）
      onUnhandledRequest: 'warn',
      serviceWorker: {
        url: '/mockServiceWorker.js'
      }
    });

    const allocationControls = await import('./mocks/allocation');
    const authControls = await import('./mocks/auth');
    const subscriptionControls = await import('./mocks/subscription');
    const shipmentControls = await import('./mocks/shipments');
    (globalThis as LogimasyGlobal).__LOGIMASY_MSW__ = {
      enableOptimisticLockConflict: allocationControls.enableOptimisticLockConflict,
      disableOptimisticLockConflict: allocationControls.disableOptimisticLockConflict,
      resetOptimisticLockSimulation: allocationControls.resetOptimisticLockSimulation,
      setLogoutFailureStatus: authControls.setLogoutFailureStatus,
      getLogoutCallCount: authControls.getLogoutCallCount,
      resetLogoutState: authControls.resetLogoutState,
      enableUnauthenticatedMode: authControls.enableUnauthenticatedMode,
      disableUnauthenticatedMode: authControls.disableUnauthenticatedMode,
      setUserMeWarningHeader: authControls.setUserMeWarningHeader,
      resetUserMeWarningHeader: authControls.resetUserMeWarningHeader,
      setBillingResponse: subscriptionControls.setBillingResponse as (r: unknown) => void,
      setBillingStatus: subscriptionControls.setBillingStatus,
      setBillingWarningHeader: subscriptionControls.setBillingWarningHeader,
      setBillingDelay: subscriptionControls.setBillingDelay,
      resetBillingState: subscriptionControls.resetBillingState,
      setExportInstructionCount: shipmentControls.setExportInstructionCount as (p: unknown) => void,
      setExportInstructionExpectedRowCountHeader:
        shipmentControls.setExportInstructionExpectedRowCountHeader,
      setExportInstructionActualCsvRowCount: shipmentControls.setExportInstructionActualCsvRowCount,
      setExportInstructionExportStatus: shipmentControls.setExportInstructionExportStatus,
      setExportInstructionExportDelay: shipmentControls.setExportInstructionExportDelay,
      setExportInstructionExportError: shipmentControls.setExportInstructionExportError,
      setExportInstructionFailuresRemaining: shipmentControls.setExportInstructionFailuresRemaining,
      resetExportInstructionState: shipmentControls.resetExportInstructionState
    };
  } catch (error) {
    console.error('[MSW] Failed to start:', error);
  }
}

// authStore.initialize() より先に MSW が起動するように、初期化を Promise 化して公開
if (typeof window !== 'undefined') {
  window.__mswReady = startMsw();
}
